import { useRef, useState } from "react";
import { FileText, Upload, Trash2, Loader2, Download, FileImage, FileX, Share2 } from "lucide-react";
import api from "../lib/api";
import { uploadFile } from "../lib/uploads";
import { toast } from "./ui/sonner";
import { shareFile } from "../lib/shareFile";
import { useShareable } from "../hooks/useShareable";

const iconFor = (mime, name) => {
  const m = (mime || "").toLowerCase();
  const n = (name || "").toLowerCase();
  if (m.startsWith("image/")) return <FileImage className="w-4 h-4 text-sky-300" />;
  if (m === "application/pdf" || n.endsWith(".pdf")) return <FileText className="w-4 h-4 text-rose-300" />;
  return <FileText className="w-4 h-4 text-[#a0aabf]" />;
};

const fmtSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * JCDocumentsBlock — Wave 25.13.
 * Document upload area on a job card (separate from "Job photos" — these
 * are reference docs like instruction manuals, datasheets, supplier PDFs).
 *
 * Permissions (decision 2a): anyone with JC access can upload + delete.
 *
 * Backend: POST /api/jobcards/{id}/documents { data_url, filename, content_type, size }
 *          DELETE /api/jobcards/{id}/documents/{doc_id}
 *
 * Each document is stored on the JC document as a base64 data URL (same
 * wire format as photos for consistency). Cap is 15 MB per doc, 30 docs
 * per JC — enforced server-side.
 */
function JCDocRow({ doc, removing, onRemove }) {
  // [Iter 322] Resolve the effective download URL:
  //   * `doc.upload_id` (new R2/object-store path) → /api/uploads/<id>
  //   * `doc.url`       (legacy base64 data URL)  → as-is
  const href = doc.upload_id
    ? `/api/uploads/${doc.upload_id}`
    : (doc.url || "");
  const desc = {
    url: href,
    filename: doc.filename,
    contentType: doc.content_type,
    title: doc.filename || "document",
  };
  const { handlers, menu } = useShareable(desc);
  return (
    <li
      className="flex items-center gap-3 px-3 py-2 rounded border border-white/10 bg-black/20 hover:border-[#1fb6c1]/40 transition"
      data-testid={`jc-doc-row-${doc.id}`}
      {...handlers}
    >
      {menu}
      <span className="shrink-0">{iconFor(doc.content_type, doc.filename)}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white truncate" title={doc.filename}>
          {doc.filename || "Untitled"}
        </div>
        <div className="text-[10px] text-[#647087] flex items-center gap-2 flex-wrap">
          {doc.uploaded_by_name && <span>by {doc.uploaded_by_name}</span>}
          {doc.uploaded_at && <span>· {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}</span>}
          {doc.size ? <span>· {fmtSize(doc.size)}</span> : null}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          shareFile(desc);
        }}
        className="hv-btn hv-btn-ghost px-2 py-1 text-xs text-[#1fb6c1]"
        title="Share file"
        data-testid={`jc-doc-share-${doc.id}`}
      >
        <Share2 className="w-3.5 h-3.5" />
      </button>
      <a
        href={href}
        download={doc.filename || "document"}
        target="_blank"
        rel="noreferrer"
        className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
        title="Download / open"
        data-testid={`jc-doc-open-${doc.id}`}
      >
        <Download className="w-3.5 h-3.5" />
      </a>
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        className="hv-btn hv-btn-ghost px-2 py-1 text-xs text-rose-300 hover:text-rose-200"
        title="Remove"
        data-testid={`jc-doc-remove-${doc.id}`}
      >
        {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
      </button>
    </li>
  );
}


export function JCDocumentsBlock({ jobcardId, documents = [], onChanged }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState({ done: 0, total: 0 });
  const [removing, setRemoving] = useState(null);
  const isUploading = uploading.total > 0;

  const onPick = () => inputRef.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading({ done: 0, total: files.length });
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      // Per-file 15 MB sanity cap (server also enforces)
      if (f.size > 15 * 1024 * 1024) {
        toast.error(`${f.name} is larger than 15 MB. Skipped.`);
        await new Promise((r) => setTimeout(r, 0));
        continue;
      }
      try {
        // [Iter 322] Upload to R2/object storage first, then link the
        // resulting `upload_id` to the JC — new writes stay OUT of
        // Mongo as base64 blobs. Legacy data_url path retained
        // server-side for offline queue replays.
        const up = await uploadFile(f, { source: "jc_document" });
        if (!up?.id) throw new Error("Upload failed");
        await api.post(`/jobcards/${jobcardId}/documents`, {
          upload_id: up.id,
          filename: f.name,
          content_type: f.type || "",
          size: f.size,
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Could not upload ${f.name}.`);
      } finally {
        setUploading((p) => ({ ...p, done: p.done + 1 }));
      }
    }
    setUploading({ done: 0, total: 0 });
    if (e.target) e.target.value = "";
    onChanged?.();
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove "${doc.filename || 'this document'}"?`)) return;
    setRemoving(doc.id);
    try {
      await api.delete(`/jobcards/${jobcardId}/documents/${doc.id}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove document.");
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="hv-card p-5" data-testid="jc-documents-block">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-bold flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1FB6C1]" /> Documents
          </h3>
          <p className="text-[11px] text-[#647087] mt-0.5">
            Instruction manuals, datasheets, PDFs. Up to 15 MB per file.
          </p>
        </div>
        <button
          type="button"
          onClick={onPick}
          disabled={isUploading}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="jc-doc-upload-btn"
        >
          {isUploading ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Uploading {uploading.done}/{uploading.total}
            </>
          ) : (
            <><Upload className="w-3 h-3" /> Upload</>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf,image/*,.doc,.docx,.txt,.csv,.xls,.xlsx"
          onChange={onFiles}
          className="hidden"
          data-testid="jc-doc-input"
        />
      </div>

      {documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 text-center text-[#647087] text-sm border border-dashed border-white/10 rounded-md" data-testid="jc-doc-empty">
          <FileX className="w-6 h-6 mb-2 opacity-60" />
          No documents yet — drop instruction manuals or supplier PDFs here.
        </div>
      ) : (
        <ul className="space-y-1.5" data-testid="jc-doc-list">
          {documents.map((d) => (
            <JCDocRow
              key={d.id}
              doc={d}
              removing={removing === d.id}
              onRemove={() => remove(d)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
